import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  useColorModeValue,
  useToast,
  Spinner,
  Flex,
  TableContainer,
} from "@chakra-ui/react";
import { getRelatedDesigns } from "../../../InventoryServices/InventoryRelatedDesigns/RelatedDesignsAPI";

const RelatedDesignTable = ({ parentInventoryId, refreshTrigger }) => {
  const [relatedDesigns, setRelatedDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // Colors
  const headerBg = useColorModeValue("blue.500", "blue.600");
  const headerColor = useColorModeValue("white", "gray.100");
  const rowHoverBg = useColorModeValue("blue.50", "gray.700");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const subTextColor = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    fetchRelatedDesigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentInventoryId, refreshTrigger]);

  const fetchRelatedDesigns = async () => {
    if (!parentInventoryId) return;

    try {
      setLoading(true);
      const response = await getRelatedDesigns(parentInventoryId);

      if (response?.status && Array.isArray(response.data)) {
        setRelatedDesigns(response.data);
      } else {
        setRelatedDesigns([]);
      }
    } catch (error) {
      console.error("Error fetching related designs:", error);
      setRelatedDesigns([]);
      toast({
        title: "Error",
        description: "Failed to load related designs",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="lg"
        />
      </Flex>
    );
  }

  return (
    <Box border="1px solid" borderColor="gray.200" shadow="sm" borderRadius="md" overflow="hidden">
      <TableContainer>
        <Table variant="simple" size="md">
          <Thead bg={headerBg}>
            <Tr>
              <Th color={headerColor} fontSize="sm">
                S.No.
              </Th>
              <Th color={headerColor} fontSize="sm">
                Title
              </Th>
              <Th color={headerColor} fontSize="sm">
                Category
              </Th>
              <Th color={headerColor} fontSize="sm">
                Product Code
              </Th>
              <Th color={headerColor} fontSize="sm">
                Version
              </Th>
              <Th color={headerColor} fontSize="sm">
                Created At
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {relatedDesigns.length > 0 ? (
              relatedDesigns.map((design, index) => (
                <Tr
                  key={design.id || index}
                  _hover={{
                    bg: rowHoverBg,
                    transform: "scale(1.01)",
                    transition: "all 0.2s",
                  }}
                >
                  {/* Serial Number */}
                  <Td>
                    <Text fontSize="sm" fontFamily="mono" color={textColor}>
                      {index + 1}
                    </Text>
                  </Td>

                  {/* Title + Description */}
                  <Td>
                    <Text fontWeight="600" color={textColor}>
                      {design.title || "-"}
                    </Text>
                    {design.description && (
                      <Text fontSize="xs" color={subTextColor}>
                        {design.description}
                      </Text>
                    )}
                  </Td>

                  {/* Category */}
                  <Td>
                    <Text textTransform="capitalize" color={textColor}>
                      {design.category || "-"}
                    </Text>
                  </Td>

                  {/* Product Code */}
                  <Td>
                    <Text fontSize="sm" fontFamily="mono" color={textColor}>
                      {design.productCode || "-"}
                    </Text>
                  </Td>

                  {/* Version */}
                  <Td>
                    <Text color={textColor}>
                      {design.version !== undefined ? design.version : "-"}
                    </Text>
                  </Td>

                  {/* Created At */}
                  <Td>
                    <Text fontSize="sm" color={textColor}>
                      {formatDate(design.created_at)}
                    </Text>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={6} textAlign="center" py={8}>
                  <Text color={subTextColor} fontStyle="italic">
                    No data found
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RelatedDesignTable;
